import { Input as InputPrimitive } from '@base-ui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { mergeStyling } from '../../common-functions';

const Input = ({ className, type, ...props }: React.ComponentProps<'input'>) => {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={mergeStyling(
        'border-gray-4 focus-visible:border-primary-2 aria-invalid:border-error disabled:bg-gray-5 h-10 md:h-6 border bg-transparent px-2.5 py-1.5 md:py-1 text-base md:text-sm file:h-10 md:file:h-6 file:text-sm file:font-medium file:text-gray-1 placeholder:text-gray-3 w-full min-w-0 outline-hidden file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  );
};

const Textarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <textarea
      data-slot="textarea"
      className={mergeStyling(
        'border-gray-4 focus-visible:border-primary-2 aria-invalid:border-error disabled:bg-gray-5 border bg-transparent px-2.5 py-2 text-base md:text-sm placeholder:text-gray-3 flex field-sizing-content min-h-16 w-full outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  );
};

const InputGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={mergeStyling(
        'border-gray-4 has-[[data-slot=input-group-control]:focus-visible]:border-primary-2 has-[[data-slot][aria-invalid=true]]:border-error has-disabled:bg-gray-5 h-10 md:h-6 border in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0 has-disabled:opacity-50 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5 group/input-group relative flex w-full min-w-0 items-center outline-hidden has-[>textarea]:h-auto',
        className
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  "h-auto py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-5 md:[&>svg:not([class*='size-'])]:size-4 flex cursor-text items-center justify-center select-none",
  {
    variants: {
      align: {
        'inline-start': 'pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem] order-first',
        'inline-end': 'pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem] order-last',
        'block-start':
          'px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start',
        'block-end': 'px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={mergeStyling(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      className={mergeStyling(
        "text-gray-2 gap-2 text-sm [&_svg:not([class*='size-'])]:size-5 md:[&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none",
        className
      )}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: React.ComponentProps<'input'>) => {
  return (
    <Input
      data-slot="input-group-control"
      className={mergeStyling(
        'flex-1 px-2.5 cursor-pointer border-0 bg-transparent text-base md:text-sm placeholder:text-gray-3 outline-hidden disabled:cursor-not-allowed disabled:opacity-50 min-w-0',
        className
      )}
      {...props}
    />
  );
};

const InputGroupTextarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <Textarea
      data-slot="input-group-control"
      className={mergeStyling(
        'flex-1 px-2.5 py-2 border-0 bg-transparent text-base md:text-sm placeholder:text-gray-3 outline-hidden disabled:cursor-not-allowed disabled:opacity-50 resize-none',
        className
      )}
      {...props}
    />
  );
};

export { Input, Textarea, InputGroup, InputGroupAddon, InputGroupText, InputGroupInput, InputGroupTextarea };
